/* ============================================================
   US-Parts24 — interactions (loaded once, deferred)
   - Scroll reveals (safe: never hides already-visible content,
     skipped in theme editor + reduced-motion → text never invisible)
   - Cursor-follow glow on dark surfaces
   - NHTSA vPIC VIN decoder (free, no key) with graceful fallback
   ============================================================ */
(function () {
  'use strict';

  var inEditor = window.Shopify && window.Shopify.designMode;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia && window.matchMedia('(pointer: fine)').matches;

  /* ---------- 1. Scroll reveals ---------------------------- */
  function initReveals() {
    if (inEditor || reduceMotion || !('IntersectionObserver' in window)) return;

    var selector = [
      '.usp-section-head',
      '.usp-benefit', '.usp-how__step', '.usp-cat', '.usp-brand',
      '.usp-card', '.usp-faq__item', '.usp-text__body',
      '.usp-reqcta', '.usp-contact', '.usp-fitment__head', '.usp-fitment__details',
      '.usp-acc__item', '.usp-trust li', '.usp-stat',
      '.usp-hero__copy > *', '.usp-hero__box'
    ].join(',');

    var vh = window.innerHeight || document.documentElement.clientHeight;
    var nodes = Array.prototype.slice.call(document.querySelectorAll(selector));
    var parentCounters = new WeakMap();

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('usp-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    nodes.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      // Already visible on load → leave untouched (no flash, never hidden)
      if (rect.top < vh * 0.92) return;
      var parent = el.parentElement;
      var idx = parentCounters.get(parent) || 0;
      parentCounters.set(parent, idx + 1);
      el.style.setProperty('--usp-rd', Math.min(idx * 70, 350) + 'ms');
      el.classList.add('usp-reveal');
      io.observe(el);
    });
  }

  /* ---------- 1b. One-shot Diagnose-Scan (.usp-scan) -------
     Rote Scan-Linie fährt EINMAL, sobald das Element in den Viewport
     scrollt. Kein Verstecken von Inhalt → nie unsichtbar. Editor +
     reduced-motion bekommen nichts. */
  function initScans() {
    if (inEditor || reduceMotion || !('IntersectionObserver' in window)) return;
    var nodes = document.querySelectorAll('.usp-scan');
    if (!nodes.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-scanned');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.25 });
    Array.prototype.forEach.call(nodes, function (el) { io.observe(el); });
  }

  /* ---------- 1b. Boot-Sequenz (R20) ------------------------
     [data-usp-boot] (How-it-works-Schritte): beim Reinscrollen .is-live
     setzen → Status-LEDs füllen sich nacheinander grün (CSS-Delays).
     reduced-motion/Editor/alte Browser: sofort .is-live (statisch grün). */
  function initBootSeq() {
    var els = document.querySelectorAll('[data-usp-boot]');
    if (!els.length) return;
    if (inEditor || reduceMotion || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(els, function (el) { el.classList.add('is-live'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-live');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.35 });
    Array.prototype.forEach.call(els, function (el) { io.observe(el); });
  }

  /* ---------- 2. Cursor-follow glow ------------------------ */
  function initGlow() {
    if (!finePointer) return;
    var targets = document.querySelectorAll('[data-usp-glow]');
    if (!targets.length) return;

    targets.forEach(function (el) {
      var raf = null, lastX = 0, lastY = 0;
      el.addEventListener('pointermove', function (e) {
        var rect = el.getBoundingClientRect();
        lastX = e.clientX - rect.left;
        lastY = e.clientY - rect.top;
        if (raf) return;
        raf = requestAnimationFrame(function () {
          el.style.setProperty('--usp-mx', lastX + 'px');
          el.style.setProperty('--usp-my', lastY + 'px');
          el.classList.add('usp-glow-on');
          raf = null;
        });
      });
      el.addEventListener('pointerleave', function () {
        el.classList.remove('usp-glow-on');
      });
    });
  }

  /* ---------- 3. VIN decoder (NHTSA vPIC) ------------------ */
  var VIN_RE = /^[A-HJ-NPR-Z0-9]{17}$/i; // valid VIN chars (no I,O,Q)
  var vinCache = {};

  function titleCase(s) {
    if (!s) return '';
    return s.toLowerCase().replace(/\b([a-z])/g, function (m, c) { return c.toUpperCase(); });
  }

  function buildEngine(r) {
    var parts = [];
    if (r.DisplacementL) parts.push(parseFloat(r.DisplacementL).toFixed(1).replace('.0', '') + ' L');
    if (r.EngineCylinders) parts.push(r.EngineCylinders + '-Zyl.');
    if (r.FuelTypePrimary && /gasoline|diesel|electric|hybrid|flex/i.test(r.FuelTypePrimary)) {
      parts.push(r.FuelTypePrimary.replace('Gasoline', 'Benzin').replace('Diesel', 'Diesel'));
    }
    return parts.join(' · ');
  }

  var FUEL_DE = { 'Gasoline': 'Benzin', 'Diesel': 'Diesel', 'Electric': 'Elektro', 'Flexible Fuel Vehicle (FFV)': 'Flex-Fuel' };

  // vPIC liefert den Antrieb sperrig ("4WD/4-Wheel Drive/4x4") → knappe DE-Form.
  function shortDrive(s) {
    if (!s) return '';
    var t = s.toLowerCase();
    if (t.indexOf('4wd') >= 0 || t.indexOf('4x4') >= 0 || t.indexOf('4-wheel') >= 0 || t.indexOf('four') >= 0) return '4×4';
    if (t.indexOf('awd') >= 0 || t.indexOf('all-wheel') >= 0 || t.indexOf('all wheel') >= 0) return 'Allrad';
    if (t.indexOf('rwd') >= 0 || t.indexOf('rear') >= 0) return 'Heckantrieb';
    if (t.indexOf('fwd') >= 0 || t.indexOf('front') >= 0) return 'Frontantrieb';
    return s;
  }

  function setField(scope, key, value) {
    var el = scope.querySelector('[data-usp-fill="' + key + '"]');
    if (el && value && !el.value) { el.value = value; el.dispatchEvent(new Event('change', { bubbles: true })); }
  }
  function setDetail(scope, key, value) {
    var el = scope.querySelector('[data-usp-detail="' + key + '"]');
    if (el) el.value = value || '';
  }

  // "Fahrzeug erkannt"-Karte (nur wo ein [data-usp-vin-card] existiert, z. B. Hero).
  function hideVinCard(scope) {
    var card = scope && scope.querySelector && scope.querySelector('[data-usp-vin-card]');
    if (card) { card.hidden = true; card.classList.remove('is-in'); card.innerHTML = ''; }
  }

  function decodeVin(input) {
    var scope = input.closest('[data-usp-vin-scope]') || input.form || document;
    var status = scope.querySelector('[data-usp-vin-status]');
    var raw = (input.value || '').toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');

    if (raw.length !== 17 || !VIN_RE.test(raw)) {
      if (status) { status.textContent = ''; status.className = 'usp-vin-status'; }
      if (scope && scope.classList) scope.classList.remove('usp-scanning');
      hideVinCard(scope);
      return Promise.resolve();
    }

    function show(cls, text) {
      if (!status) return;
      status.className = ('usp-vin-status ' + cls).trim();
      status.textContent = text;
    }

    if (vinCache[raw]) { applyResult(vinCache[raw]); return Promise.resolve(); }
    if (scope && scope.classList) scope.classList.add('usp-scanning');
    show('usp-vin-load', '▸ Scanne VIN … dekodiere Fahrzeug …');

    // Gibt ein Promise zurück, damit der Submit-Guard auf den Decode warten kann
    return fetch('https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValues/' + raw + '?format=json')
      .then(function (res) { if (!res.ok) throw new Error('http'); return res.json(); })
      .then(function (data) {
        var r = (data && data.Results && data.Results[0]) || {};
        vinCache[raw] = r;
        applyResult(r);
      })
      .catch(function () {
        if (scope && scope.classList) scope.classList.remove('usp-scanning');
        hideVinCard(scope);
        show('usp-vin-err', 'Auto-Erkennung gerade nicht verfügbar – fülle die Felder einfach manuell aus.');
      });

    function applyResult(r) {
      var make = titleCase(r.Make);
      var model = r.Model || '';
      var year = r.ModelYear || '';
      var engine = buildEngine(r);
      if (!make && !model) {
        if (scope && scope.classList) scope.classList.remove('usp-scanning');
        hideVinCard(scope);
        show('usp-vin-err', 'VIN nicht eindeutig erkannt – fülle die Felder bitte manuell aus.');
        return;
      }
      var fuel = FUEL_DE[r.FuelTypePrimary] || r.FuelTypePrimary || '';
      var hp = r.EngineHP ? (Math.round(parseFloat(r.EngineHP)) + ' hp') : '';
      var shortLabel = (make + ' ' + model).replace(/\s+/g, ' ').trim() + (year ? ' (' + year + ')' : '');

      // Visible fields — fill only if empty (don't clobber manual input)
      setField(scope, 'brand', make);
      setField(scope, 'model', model);
      setField(scope, 'year', year);
      setField(scope, 'engine', engine);

      // Hidden detail fields → full breakdown into the email (overwrite)
      setDetail(scope, 'vehicle', shortLabel);
      setDetail(scope, 'engine', engine); // Motorisierung (Hubraum · Zyl. · Kraftstoff) — auch auf Hero/Modal ohne sichtbares Motor-Feld
      setDetail(scope, 'bodyclass', r.BodyClass);
      setDetail(scope, 'drive', r.DriveType);
      setDetail(scope, 'hp', hp);
      setDetail(scope, 'transmission', r.TransmissionStyle);
      setDetail(scope, 'fuel', fuel);
      setDetail(scope, 'trim', r.Trim || r.Series2);
      setDetail(scope, 'series', r.Series);
      setDetail(scope, 'manufacturer', r.Manufacturer);
      setDetail(scope, 'plant', [r.PlantCity, r.PlantState, r.PlantCountry].filter(Boolean).join(', '));
      setDetail(scope, 'doors', r.Doors);
      setDetail(scope, 'vehicletype', r.VehicleType);

      // Remember for "save as my vehicle"
      scope.__uspVeh = {
        make: make, model: model, year: year ? parseInt(year, 10) : null,
        disp: r.DisplacementL ? parseFloat(r.DisplacementL) : null,
        cyl: r.EngineCylinders ? parseInt(r.EngineCylinders, 10) : null,
        label: shortLabel, vin: raw
      };
      ensureSaveBtn(scope, status);

      var chip = [shortLabel, r.BodyClass, engine, shortDrive(r.DriveType)].filter(Boolean).join(' · ');
      if (scope && scope.classList) scope.classList.remove('usp-scanning');

      // Wenn ein Karten-Container da ist (Hero): schönes "Fahrzeug erkannt"-Panel
      // statt der reinen Text-Statuszeile. Sonst wie bisher die Statuszeile.
      var card = scope.querySelector && scope.querySelector('[data-usp-vin-card]');
      if (card) {
        // Diagnose-Report: Status-LED + Spec-Zeilen (Terminal-Look, Zeile für Zeile)
        var specPairs = [['Aufbau', r.BodyClass], ['Motor', engine], ['Leistung', hp], ['Antrieb', shortDrive(r.DriveType)]];
        var rows = '', ri = 0;
        for (var si = 0; si < specPairs.length; si++) {
          if (!specPairs[si][1]) continue;
          rows += '<span class="usp-vehcard__row" style="--i:' + ri + '">'
            + '<span class="usp-vehcard__k">' + specPairs[si][0] + '</span>'
            + '<span class="usp-vehcard__v">' + esc(specPairs[si][1]) + '</span></span>';
          ri++;
        }
        card.innerHTML =
          '<div class="usp-vehcard__head">'
          + '<span class="usp-vehcard__led" aria-hidden="true"></span>'
          + '<span class="usp-vehcard__eyebrow">Fahrzeug erkannt · Diagnose OK</span>'
          + '</div>'
          + '<strong class="usp-vehcard__name">' + esc(shortLabel) + '</strong>'
          + (rows ? '<div class="usp-vehcard__specs">' + rows + '</div>' : '');
        card.hidden = false;
        void card.offsetWidth; // reflow → Reveal-Animation greift
        card.classList.add('is-in');
        if (status) { status.textContent = ''; status.className = 'usp-vin-status'; }
      } else {
        show('usp-vin-ok', '✓ ' + chip);
      }
    }
  }

  // Submit-Guard + Aufräumen:
  // 1) Steht eine 17-stellige VIN im Feld, aber der Decode ist noch nicht durch
  //    (Fahrzeug-Detailfeld leer), wird der Submit kurz angehalten, die VIN
  //    dekodiert (max. 4s) und dann automatisch gesendet → Motor & Co. stehen
  //    garantiert in der Mail. Bei Decode-Fehler wird trotzdem gesendet (VIN
  //    selbst ist ja enthalten).
  // 2) Leere versteckte Detailfelder werden deaktiviert → saubere Mails.
  function initFormCleanup() {
    Array.prototype.forEach.call(document.querySelectorAll('form'), function (form) {
      if (!form.querySelector('[data-usp-detail]')) return;
      form.addEventListener('submit', function (e) {
        var vinEl = form.querySelector('[data-usp-vin]');
        if (vinEl && !form.__uspGuardDone) {
          var raw = (vinEl.value || '').toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');
          var veh = form.querySelector('[data-usp-detail="vehicle"]');
          if (raw.length === 17 && veh && !veh.value) {
            e.preventDefault();
            var fired = false;
            var go = function () {
              if (fired) return;
              fired = true;
              form.__uspGuardDone = true;
              if (form.requestSubmit) { form.requestSubmit(); } else { form.submit(); }
            };
            try { Promise.resolve(decodeVin(vinEl)).then(go, go); } catch (_) { go(); }
            window.setTimeout(go, 4000);
            return;
          }
        }
        form.__uspGuardDone = true;
        Array.prototype.forEach.call(form.querySelectorAll('[data-usp-detail]'), function (el) {
          if (!el.value) el.disabled = true;
        });
      });
    });
  }

  function initVinDecoder() {
    var inputs = document.querySelectorAll('[data-usp-vin]');
    if (!inputs.length) return;
    inputs.forEach(function (input) {
      var t = null;
      input.addEventListener('input', function () {
        clearTimeout(t);
        t = setTimeout(function () { decodeVin(input); }, 550);
      });
      input.addEventListener('blur', function () { decodeVin(input); });
    });
  }

  /* ---------- 4. Hero background video --------------------- */
  function initHeroVideo() {
    var vids = document.querySelectorAll('.usp-hero__video');
    Array.prototype.forEach.call(vids, function (v) {
      v.muted = true;
      v.defaultMuted = true;
      v.playsInline = true;
      v.setAttribute('muted', '');
      v.setAttribute('playsinline', '');
      var ready = function () {
        var hero = v.closest('.usp-hero');
        if (hero) hero.classList.add('usp-hero--video-ready');
      };
      var attempt = function () {
        var p = v.play();
        if (p && p.catch) p.catch(function () {/* autoplay blocked → poster stays */});
      };
      if (v.readyState >= 2) { attempt(); ready(); }
      v.addEventListener('loadeddata', function () { attempt(); ready(); }, { once: true });
      v.addEventListener('canplay', attempt, { once: true });
      v.addEventListener('playing', ready, { once: true });
    });
  }

  /* ---------- 5. Count-up numbers -------------------------- */
  function initCounters() {
    var els = document.querySelectorAll('[data-usp-count]');
    if (!els.length) return;
    function run(el) {
      var target = parseFloat(el.getAttribute('data-usp-count')) || 0;
      var prefix = el.getAttribute('data-usp-prefix') || '';
      var suffix = el.getAttribute('data-usp-suffix') || '';
      var decimals = (String(target).split('.')[1] || '').length;
      // Optionales Cockpit-Ziffernblatt im selben .usp-stat — Zeiger + Bogen
      // laufen synchron zum Hochzählen (dekorativ, koppelt an denselben eased-Fortschritt).
      var stat = el.closest ? el.closest('.usp-stat') : null;
      var gauge = stat ? stat.querySelector('[data-usp-gauge]') : null;
      var gArc = gauge ? gauge.querySelector('.usp-gauge__val') : null;
      var gMarker = gauge ? gauge.querySelector('.usp-gauge__marker') : null;
      var gNeedle = gauge ? gauge.querySelector('.usp-gauge__needle') : null;
      var gFrac = gauge ? (parseFloat(gauge.getAttribute('data-usp-gauge-frac')) || 0) : 0;
      var GLEN = 251.33; // Bogenlänge Halbkreis r=80 (π·80)
      function setGauge(cf) {
        if (gArc) gArc.setAttribute('stroke-dashoffset', (GLEN * (1 - cf)).toFixed(2));
        // Marker sitzt IMMER auf dem äußeren Bogen (r=80 um den Hub 100,110) an der
        // aktuellen Füllspitze — kreuzt die zentrale Zahl nie (Text bleibt 100% lesbar).
        if (gMarker) {
          var a = Math.PI * (1 - cf);
          gMarker.setAttribute('cx', (100 + 80 * Math.cos(a)).toFixed(2));
          gMarker.setAttribute('cy', (110 - 80 * Math.sin(a)).toFixed(2));
        }
        // R22: echter Zeiger schwenkt mit — -90° = 0 % (links) bis +90° = 100 % (rechts).
        if (gNeedle) gNeedle.setAttribute('transform', 'rotate(' + (-90 + 180 * cf).toFixed(2) + ' 100 110)');
      }
      if (reduceMotion) { el.textContent = prefix + target.toLocaleString('de-DE') + suffix; setGauge(gFrac); return; }
      var start = null, dur = 1400;
      function frame(ts) {
        if (!start) start = ts;
        var p = Math.min((ts - start) / dur, 1);
        var eased = 1 - Math.pow(1 - p, 3);
        var val = target * eased;
        el.textContent = prefix + val.toLocaleString('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + suffix;
        setGauge(gFrac * eased);
        if (p < 1) requestAnimationFrame(frame);
        else { el.textContent = prefix + target.toLocaleString('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + suffix; setGauge(gFrac); }
      }
      requestAnimationFrame(frame);
    }
    if (!('IntersectionObserver' in window)) { Array.prototype.forEach.call(els, run); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) { run(e.target); io.unobserve(e.target); } });
    }, { threshold: 0.4 });
    Array.prototype.forEach.call(els, function (el) { io.observe(el); });
  }

  /* ========================================================
     6. GARAGE ("Mein Fahrzeug") + Fitment-Check  — no app
     Storage: localStorage. Match against custom.fitment_rules_text
     Rule format: "Make, Model, Engine, YYYY-YYYY" (engine empty = any)
     ======================================================== */
  var GKEY = 'usp_garage_vehicle_v1';
  function gGet() { try { return JSON.parse(localStorage.getItem(GKEY) || 'null'); } catch (e) { return null; } }
  function gSet(v) { try { localStorage.setItem(GKEY, JSON.stringify(v)); } catch (e) {} document.dispatchEvent(new CustomEvent('usp:garage')); }
  function gClear() { try { localStorage.removeItem(GKEY); } catch (e) {} document.dispatchEvent(new CustomEvent('usp:garage')); }

  var MAKE_ALIAS = { chevy: 'chevrolet', 'mercedes-benz': 'mercedes', vw: 'volkswagen' };
  function nMake(s) { s = (s || '').toLowerCase().trim().replace(/\./g, ''); return MAKE_ALIAS[s] || s; }
  function nModel(s) { return (s || '').toLowerCase().replace(/[\s\-_/]/g, ''); }

  function engSig(s) {
    s = s || '';
    var d = null, c = null;
    var md = s.match(/(\d(?:\.\d)?)\s*l\b/i); if (md) d = parseFloat(md[1]);
    var mv = s.match(/\bV[\s-]?(\d{1,2})\b/i); var mi = s.match(/\bI[\s-]?(\d)\b/i);
    if (mv) c = parseInt(mv[1], 10); else if (mi) c = parseInt(mi[1], 10);
    return { d: d, c: c };
  }
  function yRange(s) {
    s = s || '';
    var m = s.match(/(\d{4})\s*[-–]\s*(\d{4})/); if (m) return [+m[1], +m[2]];
    m = s.match(/(\d{4})\s*\+/); if (m) return [+m[1], 9999];
    m = s.match(/(\d{4})/); if (m) return [+m[1], +m[1]];
    return null;
  }
  function parseRule(line) {
    var p = line.split(',').map(function (x) { return x.trim(); });
    if (p.length < 2) return null;
    var make = p[0], model = p[1], engine = '', yr = null;
    if (p.length >= 5 && /^\d{4}$/.test(p[2]) && /^\d{4}$/.test(p[3])) {
      yr = [+p[2], +p[3]]; engine = p[4] || '';
    } else {
      engine = p[2] || ''; yr = yRange(p[3] || '');
      if (!yr && /\d{4}/.test(engine)) { yr = yRange(engine); engine = ''; }
    }
    return { make: make, model: model, engine: engine, yr: yr };
  }
  function modelMatch(a, b) {
    if (!a || !b) return false;
    if (a === b) return true;
    return a.length >= 3 && b.length >= 3 && (a.indexOf(b) >= 0 || b.indexOf(a) >= 0);
  }
  function engMatch(ruleEng, veh) {
    if (!ruleEng) return true;            // "Motor egal"
    var rs = engSig(ruleEng);
    if (rs.d == null && rs.c == null) return null;
    var dispOk = (rs.d != null && veh.disp != null) ? Math.abs(rs.d - veh.disp) <= 0.15 : null;
    var cylOk = (rs.c != null && veh.cyl != null) ? rs.c === veh.cyl : null;
    if (dispOk === false || cylOk === false) return false;
    if (dispOk === true || cylOk === true) return true;
    return null;
  }
  function matchOne(rule, veh) {
    if (nMake(rule.make) !== veh.makeN) return 'none';
    if (!modelMatch(nModel(rule.model), veh.modelN)) return 'none';
    if (rule.yr && veh.year) { if (veh.year < rule.yr[0] || veh.year > rule.yr[1]) return 'none'; }
    var e = engMatch(rule.engine, veh);
    if (e === true) return (rule.yr && !veh.year) ? 'maybe' : 'fit';
    return 'maybe'; // engine unknown or mismatch → cautious, never hard-no
  }
  function matchFitment(text, veh) {
    var lines = (text || '').split(/\r?\n/).map(function (x) { return x.trim(); }).filter(Boolean);
    var best = 'none';
    for (var i = 0; i < lines.length; i++) {
      var r = parseRule(lines[i]); if (!r) continue;
      var m = matchOne(r, veh);
      if (m === 'fit') return 'fit';
      if (m === 'maybe') best = 'maybe';
    }
    return best;
  }
  function vehNorm(o) {
    return {
      make: o.make, model: o.model, year: o.year ? parseInt(o.year, 10) : null,
      disp: o.disp != null ? parseFloat(o.disp) : null, cyl: o.cyl != null ? parseInt(o.cyl, 10) : null,
      engine: o.engine || '', // R21: Motor-Label (z. B. "3.6L V6") für Chip-Anzeige
      label: o.label || ((o.make || '') + ' ' + (o.model || '') + (o.year ? ' (' + o.year + ')' : '')).trim(),
      vin: o.vin || '', makeN: nMake(o.make), modelN: nModel(o.model)
    };
  }

  // "Als mein Fahrzeug speichern" button after a successful decode
  function ensureSaveBtn(scope, status) {
    if (!scope || scope.querySelector('.usp-garage-save')) return;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'usp-garage-save';
    btn.textContent = '🚗 Fahrzeug merken';
    btn.title = 'Wird in diesem Browser gespeichert – damit wir dir auf Produktseiten direkt zeigen, ob ein Teil passt.';
    btn.addEventListener('click', function () {
      if (!scope.__uspVeh) return;
      gSet(vehNorm(scope.__uspVeh));
      btn.textContent = '✓ Gemerkt – in diesem Browser gespeichert';
      btn.classList.add('is-saved');
    });
    if (status && status.parentNode) status.parentNode.insertBefore(btn, status.nextSibling);
    else scope.appendChild(btn);
  }

  // Render the garage chip(s) + PDP fit badge
  function renderGarage() {
    var veh = gGet();

    Array.prototype.forEach.call(document.querySelectorAll('[data-usp-garage-chip]'), function (chip) {
      if (veh) {
        // R21: Diagnose-Zeile statt Emoji-Chip — grüne LED + Mono-Label (+ Motor,
        // falls gewählt), Aktionen rechts als kompakte Ghost-Buttons.
        chip.innerHTML = '<span class="usp-gchip"><span class="usp-gchip__led" aria-hidden="true"></span>'
          + '<span class="usp-gchip__k">Fahrzeug</span>'
          + '<span class="usp-gchip__txt"><strong></strong><em class="usp-gchip__eng" hidden></em></span>'
          + '<span class="usp-gchip__actions">'
          + '<button type="button" class="usp-gchip__edit" data-usp-garage-open>Ändern</button>'
          + '<button type="button" class="usp-gchip__x" data-usp-garage-clear aria-label="Fahrzeug entfernen" title="Fahrzeug entfernen">✕</button>'
          + '</span></span>';
        chip.querySelector('strong').textContent = veh.label || (veh.make + ' ' + veh.model);
        if (veh.engine) {
          var ee = chip.querySelector('.usp-gchip__eng');
          ee.textContent = ' · ' + veh.engine; ee.hidden = false;
        }
        chip.hidden = false;
      } else {
        // No vehicle yet → keep the chip empty/hidden; the fit-result box is the single prompt
        chip.innerHTML = '';
        chip.hidden = true;
      }
    });

    Array.prototype.forEach.call(document.querySelectorAll('[data-usp-fitment-result]'), function (box) {
      var compact = box.classList.contains('usp-atc-fit');
      var sec = box.closest('.shopify-section') || document;
      var rulesEl = sec.querySelector('[data-usp-fitment-rules]') || document.querySelector('[data-usp-fitment-rules]');
      var rules = rulesEl ? (rulesEl.textContent || '') : '';
      function cta(label) {
        return compact
          ? '<button type="button" class="usp-fitresult__link" data-usp-request-open>' + label + ' →</button>'
          : '<button type="button" class="usp-btn usp-btn--primary usp-fitresult__cta" data-usp-request-open>' + label + '</button>';
      }
      function set(cls, html) { box.className = 'usp-fitresult ' + cls + (compact ? ' usp-atc-fit' : ''); box.innerHTML = html; }

      if (!veh) {
        set('usp-fitresult--neutral', '🚗 <strong>Dein Fahrzeug?</strong> Speichere es für den automatischen Passt-Check. '
          + '<button type="button" class="usp-fitresult__link" data-usp-garage-open>Fahrzeug wählen →</button>');
        return;
      }
      var L = esc(veh.label);
      var state = rules.trim() ? matchFitment(rules, veh) : 'nodata';
      if (state === 'fit') {
        set('usp-fitresult--fit', '✓ <strong>Passt zu deinem ' + L + '</strong>');
      } else if (state === 'maybe') {
        set('usp-fitresult--maybe', '≈ Passt vermutlich zu deinem <strong>' + L + '</strong> – Variante kurz prüfen. ' + cta('Jetzt prüfen lassen'));
      } else { // none or nodata → NEUTRAL, never red, with strong request CTA
        set('usp-fitresult--neutral', 'Für <strong>' + L + '</strong> noch nicht bestätigt – wir prüfen es kurz für dich. ' + cta('Jetzt anfragen'));
      }
    });

    renderCardFitment(veh);
  }

  // Produktkarten-Fitment-Chip: rendert NUR bei gemerktem Fahrzeug UND
  // Regel-Treffer (fit/maybe) → keine Grid-Unruhe ohne Daten/ohne Fahrzeug.
  function renderCardFitment(veh) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-usp-card-fit]'), function (box) {
      var rules = box.getAttribute('data-usp-card-rules') || '';
      if (!veh || !rules.trim()) { box.hidden = true; box.innerHTML = ''; return; }
      var state = matchFitment(rules, veh);
      if (state === 'fit') {
        box.innerHTML = '<span class="usp-fit usp-fit--ok"><span class="usp-fit__led" aria-hidden="true"></span>PASST</span>';
        box.hidden = false;
      } else if (state === 'maybe') {
        box.innerHTML = '<span class="usp-fit usp-fit--maybe"><span class="usp-fit__led" aria-hidden="true"></span>PRÜFEN</span>';
        box.hidden = false;
      } else { box.hidden = true; box.innerHTML = ''; }
    });
  }
  function esc(s) { return (s || '').replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  // Manual cascading picker (Make → Model → Year → Engine) from window.USP_VEHICLES.
  // R21: Motor als 4. Stufe (OPTIONAL, „Weiß ich nicht") — ein bekannter Motor macht
  // den Fitment-Check präzise (PASST statt PRÜFEN), ohne ihn kommt man trotzdem durch.
  function openPicker() {
    var data = window.USP_VEHICLES || [];
    var overlay = document.createElement('div');
    overlay.className = 'usp-picker';
    overlay.innerHTML =
      '<div class="usp-picker__box usp-hud" role="dialog" aria-modal="true" aria-label="Fahrzeug wählen">'
      + '<button type="button" class="usp-picker__close" aria-label="Schließen">✕</button>'
      + '<div class="usp-term__head"><span class="usp-term__led" aria-hidden="true"></span><span class="usp-term__title">Diagnose · Fahrzeugwahl</span></div>'
      + '<h3 class="usp-picker__title">Mein Fahrzeug wählen</h3>'
      + '<label class="usp-label">Marke</label><select class="usp-select" data-p="make"></select>'
      + '<label class="usp-label">Modell</label><select class="usp-select" data-p="model" disabled></select>'
      + '<label class="usp-label">Baujahr</label><select class="usp-select" data-p="year" disabled></select>'
      + '<label class="usp-label">Motor <span class="usp-picker__opt">(optional)</span></label><select class="usp-select" data-p="engine" disabled></select>'
      + '<button type="button" class="usp-btn usp-btn--primary usp-btn--block usp-picker__save" disabled>Fahrzeug merken</button>'
      + '<p class="usp-note" style="text-align:center">Wird in diesem Browser gespeichert – kein Konto nötig.</p>'
      + (data.length ? '' : '<p class="usp-note">Fahrzeugliste konnte nicht geladen werden – du kannst trotzdem per VIN anfragen.</p>')
      + '</div>';
    document.body.appendChild(overlay);
    var mk = overlay.querySelector('[data-p="make"]'),
        md = overlay.querySelector('[data-p="model"]'),
        yr = overlay.querySelector('[data-p="year"]'),
        eng = overlay.querySelector('[data-p="engine"]'),
        save = overlay.querySelector('.usp-picker__save');

    var makes = Array.from(new Set(data.map(function (r) { return r[0]; }))).sort();
    mk.innerHTML = '<option value="">Bitte wählen</option>' + makes.map(function (m) { return '<option>' + esc(m) + '</option>'; }).join('');

    function resetEng() { eng.disabled = true; eng.innerHTML = ''; }
    mk.addEventListener('change', function () {
      var models = Array.from(new Set(data.filter(function (r) { return r[0] === mk.value; }).map(function (r) { return r[1]; }))).sort();
      md.innerHTML = '<option value="">Bitte wählen</option>' + models.map(function (m) { return '<option>' + esc(m) + '</option>'; }).join('');
      md.disabled = !mk.value; yr.disabled = true; yr.innerHTML = ''; resetEng(); save.disabled = true;
    });
    md.addEventListener('change', function () {
      var rows = data.filter(function (r) { return r[0] === mk.value && r[1] === md.value; });
      var min = Math.min.apply(null, rows.map(function (r) { return r[2]; }));
      var max = Math.max.apply(null, rows.map(function (r) { return r[3]; }));
      var years = []; for (var y = max; y >= min; y--) years.push(y);
      yr.innerHTML = '<option value="">Bitte wählen</option>' + years.map(function (y) { return '<option>' + y + '</option>'; }).join('');
      yr.disabled = !md.value; resetEng(); save.disabled = true;
    });
    yr.addEventListener('change', function () {
      save.disabled = !yr.value;
      // Motoren, deren Bauzeitraum das gewählte Jahr enthält
      var y = parseInt(yr.value, 10);
      var engines = Array.from(new Set(data.filter(function (r) {
        return r[0] === mk.value && r[1] === md.value && r[2] <= y && y <= r[3] && r[4];
      }).map(function (r) { return r[4]; }))).sort();
      if (yr.value && engines.length) {
        eng.innerHTML = '<option value="">Weiß ich nicht / egal</option>' + engines.map(function (e) { return '<option>' + esc(e) + '</option>'; }).join('');
        eng.disabled = false;
      } else { resetEng(); }
    });
    save.addEventListener('click', function () {
      var sig = eng.value ? engSig(eng.value) : { d: null, c: null };
      gSet(vehNorm({
        make: mk.value, model: md.value, year: yr.value,
        engine: eng.value, disp: sig.d, cyl: sig.c,
        label: mk.value + ' ' + md.value + ' (' + yr.value + ')'
      }));
      close();
    });
    function close() { overlay.remove(); }
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
    overlay.querySelector('.usp-picker__close').addEventListener('click', close);
  }

  function initGarage() {
    renderGarage();
    document.addEventListener('click', function (e) {
      var open = e.target.closest && e.target.closest('[data-usp-garage-open]');
      var clr = e.target.closest && e.target.closest('[data-usp-garage-clear]');
      if (open) { e.preventDefault(); openPicker(); }
      if (clr) { e.preventDefault(); gClear(); }
    });
  }
  document.addEventListener('usp:garage', renderGarage);

  /* ---------- 7. Per-product request modal --------------- */
  function getModal() { return document.querySelector('[data-usp-request-modal]'); }
  function setVal(scope, sel, val) { var el = scope.querySelector(sel); if (el && val != null) el.value = val; }
  function openRequestModal(teil, trigger) {
    var m = getModal(); if (!m) return;
    m.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    // Quelle präzisieren: Basis (Seite, aus Liquid) + Auslöser (Button-Kontext)
    var src = m.querySelector('[data-usp-source]');
    if (src) {
      if (!src.__uspBase) src.__uspBase = src.value;
      src.value = trigger ? (src.__uspBase + ' · Auslöser: ' + trigger) : src.__uspBase;
    }
    var p = window.USP_PRODUCT;
    var prodLine = m.querySelector('[data-usp-modal-product]');
    if (p) {
      setVal(m, '[data-usp-product]', p.title);
      setVal(m, '[data-usp-product-url]', p.url);
      setVal(m, '[data-usp-sku]', p.sku);
      var part = m.querySelector('[data-usp-part]'); if (part && !part.value) part.value = teil || p.title;
      if (prodLine) { prodLine.textContent = '🔧 ' + p.title; prodLine.hidden = false; }
    } else if (teil) {
      var part2 = m.querySelector('[data-usp-part]'); if (part2 && !part2.value) part2.value = teil;
    }
    var veh = gGet();
    var vin = m.querySelector('[data-usp-vin]');
    if (vin && veh && veh.vin && !vin.value) { vin.value = veh.vin; decodeVin(vin); }
    var f = m.querySelector('input, textarea'); if (f) try { f.focus(); } catch (e) {}
  }
  function closeRequestModal() { var m = getModal(); if (m) { m.hidden = true; document.documentElement.style.overflow = ''; } }
  function initRequestModal() {
    var m = getModal();
    var postedHere = false; // nur wieder öffnen, wenn DIESES Formular gepostet hat (URL-Anker)
    try { postedHere = !!(m && location.hash && m.querySelector(location.hash)); } catch (_) {}
    if (m && m.querySelector('[data-usp-modal-success]') && postedHere) openRequestModal();
    if (document.__uspModalBound) return; document.__uspModalBound = true;
    document.addEventListener('click', function (e) {
      var openBtn = e.target.closest && e.target.closest('[data-usp-request-open]');
      var anfrageLink = e.target.closest && e.target.closest('a[href*="/pages/anfrage"], a[href*="/pages/us-autoteile-teileanfrage"]');
      if (openBtn) {
        e.preventDefault();
        var trig =
          openBtn.closest('.usp-tabbar') ? 'Tab-Bar (mobil)'
          : (openBtn.closest('.usp-reqfab') || openBtn.classList.contains('usp-reqfab')) ? 'Schwebe-Button'
          : openBtn.closest('.usp-atc-extra') ? 'Produktseite – Kaufbox'
          : openBtn.closest('.usp-fitresult') ? 'Produktseite – Fitment-Check'
          : (openBtn.closest('.usp-drawer-cta') || openBtn.classList.contains('usp-drawer-cta')) ? 'Menü-Drawer'
          : 'Button';
        openRequestModal(null, trig);
        return;
      }
      if (anfrageLink) {
        e.preventDefault();
        var teil = null; try { teil = new URL(anfrageLink.href, location.origin).searchParams.get('teil'); } catch (_) {}
        openRequestModal(teil, 'Anfrage-Link'); return;
      }
      if (e.target.closest && e.target.closest('[data-usp-request-close]')) { e.preventDefault(); closeRequestModal(); return; }
      if (e.target.hasAttribute && e.target.hasAttribute('data-usp-request-modal')) { closeRequestModal(); }
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeRequestModal(); });
  }

  /* ---------- 9. Mobile drawer "VIN / Teil anfragen" CTA ---- */
  function injectDrawerCta() {
    var container = document.querySelector('.menu-list--mobile .menu-list__scroll-container');
    if (!container || container.querySelector('[data-usp-drawer-cta]')) return;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'usp-btn usp-btn--primary usp-drawer-cta';
    btn.setAttribute('data-usp-drawer-cta', '');
    btn.setAttribute('data-usp-request-open', '');
    btn.textContent = '🔧 VIN / Teil anfragen';
    container.insertBefore(btn, container.firstChild);
  }

  /* ---------- 8. Inject CTA + fit badge next to Add-to-Cart */
  function injectAtc() {
    if (!window.USP_PRODUCT) return;            // product pages only
    if (document.querySelector('[data-usp-atc-injected]')) return;
    var atc = document.querySelector(
      'product-form button[name="add"], form[action*="/cart/add"] [type="submit"], button[name="add"], .product-form__submit, .add-to-cart, [data-add-to-cart]'
    );
    if (!atc) return;
    var host = atc.closest('product-form, .product-form, form') || atc;
    var anfrage = '/pages/us-autoteile-teileanfrage?teil=' + encodeURIComponent(window.USP_PRODUCT.title || '');
    var wrap = document.createElement('div');
    wrap.className = 'usp-atc-extra';
    wrap.setAttribute('data-usp-atc-injected', '');
    wrap.innerHTML =
      '<div class="usp-atc-chip" data-usp-garage-chip hidden></div>'
      + '<div class="usp-atc-fit" data-usp-fitment-result data-usp-anfrage="' + esc(anfrage) + '"></div>'
      + '<button type="button" class="usp-btn usp-atc-ask" data-usp-request-open>📩 Jetzt direkt anfragen</button>';
    if (host.parentNode) host.parentNode.insertBefore(wrap, host.nextSibling);
    renderGarage();
  }

  /* ---------- Fahrzeug-Box im Hero (card_mode: vehicle) -------
     Marke → Modell → Motor aus window.USP_VEHICLES; "Teile anzeigen" setzt die
     Garage (gSet → usp:garage), der Finder darunter filtert automatisch.
     Das VIN-Feld nutzt den vorhandenen Decoder (data-usp-vin). */
  function initVehicleBoxes() {
    var boxes = document.querySelectorAll('[data-usp-vehiclebox]');
    if (!boxes.length) return;
    var data = window.USP_VEHICLES || [];
    Array.prototype.forEach.call(boxes, function (box) {
      if (box.__uspVehInit) return; box.__uspVehInit = true;
      var mk = box.querySelector('[data-usp-veh="make"]'),
          md = box.querySelector('[data-usp-veh="model"]'),
          yr = box.querySelector('[data-usp-veh="year"]'), // R21: Baujahr-Stufe (optional im Markup alter Instanzen)
          en = box.querySelector('[data-usp-veh="engine"]'),
          go = box.querySelector('[data-usp-veh-go]');
      if (!mk || !md || !en) return;
      var seen = {}, makes = [];
      data.forEach(function (r) { if (r[0] && !seen[r[0]]) { seen[r[0]] = 1; makes.push(r[0]); } });
      makes.sort();
      mk.innerHTML = '<option value="">Marke</option>' + makes.map(function (m) { return '<option>' + esc(m) + '</option>'; }).join('');
      function resetEngine() { en.disabled = true; en.innerHTML = '<option value="">Motor (optional)</option>'; }
      function fillModels() {
        var s = {}, models = [];
        data.forEach(function (r) { if (r[0] === mk.value && r[1] && !s[r[1]]) { s[r[1]] = 1; models.push(r[1]); } });
        models.sort();
        md.innerHTML = '<option value="">Modell</option>' + models.map(function (m) { return '<option>' + esc(m) + '</option>'; }).join('');
        md.disabled = !mk.value;
        if (yr) { yr.disabled = true; yr.innerHTML = '<option value="">Baujahr</option>'; }
        resetEngine();
      }
      function fillYears() {
        if (!yr) { fillEngines(); return; }
        var rows = data.filter(function (r) { return r[0] === mk.value && r[1] === md.value; });
        if (!rows.length) { yr.disabled = true; return; }
        var min = Math.min.apply(null, rows.map(function (r) { return r[2]; }));
        var max = Math.max.apply(null, rows.map(function (r) { return r[3]; }));
        var opts = []; for (var y = max; y >= min; y--) opts.push(y);
        yr.innerHTML = '<option value="">Baujahr</option>' + opts.map(function (y) { return '<option>' + y + '</option>'; }).join('');
        yr.disabled = !md.value;
        resetEngine();
      }
      function fillEngines() {
        var y = yr && yr.value ? parseInt(yr.value, 10) : null;
        var s = {}, engs = [];
        data.forEach(function (r) {
          if (r[0] !== mk.value || r[1] !== md.value || !r[4]) return;
          if (y != null && !(r[2] <= y && y <= r[3])) return; // jahresgefiltert
          if (!s[r[4]]) { s[r[4]] = 1; engs.push(r[4]); }
        });
        engs.sort();
        en.innerHTML = '<option value="">Motor (optional)</option>' + engs.map(function (e) { return '<option>' + esc(e) + '</option>'; }).join('');
        en.disabled = !md.value;
      }
      mk.addEventListener('change', fillModels);
      md.addEventListener('change', fillYears);
      if (yr) yr.addEventListener('change', fillEngines);
      var cur = gGet();
      if (cur && cur.make) {
        mk.value = cur.make;
        if (mk.value === cur.make) {
          fillModels();
          if (cur.model) {
            md.value = cur.model;
            if (md.value === cur.model) {
              fillYears();
              if (yr && cur.year) { yr.value = String(cur.year); }
              fillEngines();
              if (cur.engine) en.value = cur.engine;
            }
          }
        }
      }
      if (go) go.addEventListener('click', function () {
        var missingYear = yr && !yr.value;
        if (!mk.value || !md.value || missingYear) {
          var st = box.querySelector('[data-usp-vin-status]');
          if (st) st.textContent = missingYear ? 'Bitte auch das Baujahr wählen.' : 'Bitte Marke und Modell wählen.';
          return;
        }
        var sig = engSig(en.value || '');
        gSet(vehNorm({ make: mk.value, model: md.value, year: yr ? yr.value : null,
          engine: en.value || '', disp: sig.d, cyl: sig.c,
          label: (mk.value + ' ' + md.value + (yr && yr.value ? ' (' + yr.value + ')' : '')).trim() }));
        var target = document.querySelector('.toolbar-wrap') || document.querySelector('[data-myveh]');
        if (target) try { target.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {}
      });
    });
  }

  /* ---------- Posted-Toast: Feedback nach Formular-Post ohne sichtbare Erfolgsmeldung */
  function initPostedToast() {
    if (location.search.indexOf('contact_posted=true') === -1) return;
    var visible = false;
    document.querySelectorAll('.usp-success').forEach(function (e) { if (e.offsetParent !== null) visible = true; });
    if (visible) return; // Inline-Meldung oder offenes Modal übernimmt
    var t = document.createElement('div');
    t.className = 'usp-toast';
    t.setAttribute('role', 'status');
    t.innerHTML = '<span class="usp-toast__led" aria-hidden="true"></span> Anfrage gesendet – wir melden uns mit deinem Angebot.';
    document.body.appendChild(t);
    setTimeout(function () { t.classList.add('is-out'); }, 6000);
    setTimeout(function () { if (t.parentNode) t.parentNode.removeChild(t); }, 6600);
  }

  /* ---------- init ---------------------------------------- */
  function init() { initReveals(); initScans(); initBootSeq(); initGlow(); initVinDecoder(); initHeroVideo(); initFormCleanup(); initCounters(); initGarage(); initVehicleBoxes(); initRequestModal(); injectAtc(); injectDrawerCta(); initPostedToast(); }
  // Drawer can be lazy-rendered → retry when the mobile menu is opened
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('.header__icon--menu, .menu-drawer-container summary')) {
      setTimeout(injectDrawerCta, 60);
    }
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-run when sections are re-rendered in the theme editor
  document.addEventListener('shopify:section:load', function () { initScans(); initBootSeq(); initGlow(); initVinDecoder(); initHeroVideo(); initFormCleanup(); initCounters(); renderGarage(); initVehicleBoxes(); initRequestModal(); injectAtc(); injectDrawerCta(); });
})();
